import { useState, useMemo, useEffect } from "react"
import { Layout } from "../components/Layout"
import { DataTable } from "@/components/DataTable/DataTable"
import { TableSkeleton } from "@/components/Skeleton/Skeleton"
import { EmptyState } from "@/components/EmptyState/EmptyState"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { Button } from "@/components/Button"
import { Input } from "@/components/Form/Input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog/Dialog"
import { useToast } from "@/components/Toast"
import { useTeams, useCreateTeam } from "@/hooks/useTeams"
import { useOrganizations } from "@/hooks/useOrganizations"
import { useSubscriptionPlans } from "@/hooks/useSubscriptionPlans"
import { createTeamToast } from "@/lib/createTeamResult"
import {
  buildTeamAccessPayload,
  defaultTeamAccessChoice,
  teamAccessChoiceError,
  teamAccessConfirmCopy,
  teamAccessSubmitLabel,
  type TeamAccessChoice,
} from "@/lib/createTeamAccess"
import { TeamAccessChooser } from "@/components/TeamAccessChooser"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import type { Team } from "@/hooks/useTeams"
import { APIError } from "@/api"
import {
  UserGroupIcon,
  UsersIcon,
  CalendarIcon,
  DocumentArrowDownIcon,
  PlusIcon,
} from "@heroicons/react/24/outline"
import { ColumnDef } from "@tanstack/react-table"

export function Teams() {
  const [page, setPage] = useState(0)
  const [pageSize] = useState(50)
  // What's in the search box right now (every keystroke) vs. the term we actually
  // query with (debounced) — so we don't fire a backend request per keystroke.
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const { toast } = useToast()

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(id)
  }, [search])

  // A new search starts from page 1; otherwise the offset could point past the
  // (usually smaller) filtered result set and show an empty page.
  useEffect(() => {
    setPage(0)
  }, [debouncedSearch])

  const { data, isLoading, isPlaceholderData, error, refetch } = useTeams({
    page: page + 1,
    pageSize,
    search: debouncedSearch,
  })

  // Pulled at the top so a fresh "Create Team" dialog open doesn't trigger a fetch storm.
  const { data: orgsData } = useOrganizations({ pageSize: 100 })
  const { data: plansData } = useSubscriptionPlans()
  const createTeam = useCreateTeam()

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [createForm, setCreateForm] = useState({
    name: "",
    creatorEmail: "",
    league: "",
    level: "",
    organizationPublicId: "",
  })
  const [access, setAccess] = useState<TeamAccessChoice>(() => defaultTeamAccessChoice())
  const [showAccessConfirm, setShowAccessConfirm] = useState(false)
  const selectedPlanName = plansData?.plans?.find((plan) => plan.id === access.planId)?.name

  const handleTeamClick = (teamId: string) => {
    window.location.href = `/teams/${teamId}`
  }

  // Validate, then confirm anything that provisions in Stripe (comp or trial)
  // before creating. A plain "no plan yet" create needs no confirmation.
  const onCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.name.trim() || !createForm.creatorEmail.trim()) return
    const accessError = teamAccessChoiceError(access)
    if (accessError) {
      toast({ title: "Check the access section", description: accessError, variant: "destructive" })
      return
    }
    if (teamAccessConfirmCopy(access, createForm.name.trim(), selectedPlanName)) {
      setShowAccessConfirm(true)
      return
    }
    void runCreate()
  }

  const runCreate = async () => {
    try {
      const result = await createTeam.mutateAsync({
        name: createForm.name.trim(),
        creatorEmail: createForm.creatorEmail.trim(),
        league: createForm.league.trim() || undefined,
        level: createForm.level.trim() || undefined,
        organizationPublicId: createForm.organizationPublicId || undefined,
        type: "football",
        ...buildTeamAccessPayload(access),
      })
      toast(createTeamToast(result, result.team.name))
      setCreateForm({
        name: "",
        creatorEmail: "",
        league: "",
        level: "",
        organizationPublicId: "",
      })
      setAccess(defaultTeamAccessChoice())
      setShowCreateDialog(false)
      refetch()
    } catch (e) {
      toast({
        title: "Error creating team",
        description: e instanceof APIError ? e.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  const columns = useMemo<ColumnDef<Team>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Team Name",
        cell: ({ row }) => (
          <div className="flex items-center">
            <UserGroupIcon className="h-5 w-5 text-secondary mr-2" />
            <span 
              className="font-medium text-primary underline-offset-2 hover:underline cursor-pointer"
              data-testid={`team-name-${row.original.public_id}`}
            >
              {row.original.name}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "memberCount",
        header: "Members",
        cell: ({ row }) => (
          <div className="flex items-center">
            <UsersIcon className="h-4 w-4 text-secondary mr-1" />
            <span className="text-sm">{row.original.memberCount || 0}</span>
          </div>
        ),
      },
      {
        accessorKey: "gameCount",
        header: "Games",
        cell: ({ row }) => <span className="text-sm">{row.original.gameCount || 0}</span>,
      },
      {
        // Clips, not games, are the "has film" signal: an upload that never
        // produced clips leaves a game row behind with nothing to watch.
        accessorKey: "clipCount",
        header: "Clips",
        cell: ({ row }) =>
          row.original.clipCount > 0 ? (
            <span className="text-sm">{row.original.clipCount}</span>
          ) : (
            <span className="text-sm text-secondary">No film</span>
          ),
      },
      {
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <div className="flex items-center">
            <CalendarIcon className="h-4 w-4 text-secondary mr-1" />
            <span className="text-sm text-secondary">
              {new Date(row.original.created_at).toLocaleDateString()}
            </span>
          </div>
        ),
      },
    ],
    []
  )

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Teams</h1>
            <p className="text-secondary">Manage teams and their settings</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline">
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button onClick={() => setShowCreateDialog(true)}>
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Team
            </Button>
          </div>
        </div>

        {isLoading ? (
          <TableSkeleton rows={10} />
        ) : data?.teams?.length === 0 && !debouncedSearch ? (
          <EmptyState
            icon={<UserGroupIcon className="h-12 w-12" />}
            title="No teams found"
            description="No teams have been created yet."
            action={{
              label: "Create first team",
              onClick: () => setShowCreateDialog(true),
            }}
          />
        ) : (
          <>
            {/* Server-side search + paging: the box queries ALL teams by name,
                not just the rows loaded on the current page. */}
            <DataTable
              columns={columns}
              data={data?.teams || []}
              searchValue={search}
              onSearchChange={setSearch}
              // The server pages this table (footer below); DataTable's own pager
              // would slice each server page again, 10 rows at a time.
              showPagination={false}
              searchPlaceholder="Search by team name…"
              onRowClick={(team) => handleTeamClick(team.public_id)}
            />

            {data && data.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-secondary">
                  Showing {(data.pagination.page - 1) * data.pagination.pageSize + 1}-{Math.min(data.pagination.page * data.pagination.pageSize, data.pagination.totalCount)} of{" "}
                  {data.pagination.totalCount} teams
                </p>
                <div className="flex items-center space-x-2">
                  <button
                    className="px-3 py-1 text-sm rounded border border-border hover:bg-surface disabled:opacity-50"
                    // Placeholder rows are the previous query's page; its bounds
                    // don't apply to the page being loaded.
                    disabled={isPlaceholderData || !data.pagination.hasPreviousPage}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-secondary">
                    Page {data.pagination.page} of {data.pagination.totalPages}
                  </span>
                  <button
                    className="px-3 py-1 text-sm rounded border border-border hover:bg-surface disabled:opacity-50"
                    disabled={isPlaceholderData || !data.pagination.hasNextPage}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        {/* The access chooser makes this form tall; keep the buttons reachable
            on a short laptop screen by capping the height and scrolling the body. */}
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Team</DialogTitle>
            <DialogDescription>
              Creates a team owned by the coach below, who becomes its admin member.
              Then choose how the team gets access: comp a plan (free, no card), start
              the standard trial, or leave it on the free tier for now.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onCreateSubmit} className="space-y-4">
            <Input
              label="Team Name"
              placeholder="Springfield Tigers"
              value={createForm.name}
              onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))}
              required
            />
            <Input
              label="Creator Email"
              type="email"
              placeholder="coach@example.com"
              value={createForm.creatorEmail}
              onChange={(e) => setCreateForm((s) => ({ ...s, creatorEmail: e.target.value }))}
              required
            />
            <p className="text-xs text-secondary -mt-2">
              The user who will own the team. They become an admin member.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="League"
                placeholder="unknown"
                value={createForm.league}
                onChange={(e) => setCreateForm((s) => ({ ...s, league: e.target.value }))}
              />
              <Input
                label="Level"
                placeholder="10U"
                value={createForm.level}
                onChange={(e) => setCreateForm((s) => ({ ...s, level: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Organization (optional)</label>
              <select
                value={createForm.organizationPublicId}
                onChange={(e) =>
                  setCreateForm((s) => ({ ...s, organizationPublicId: e.target.value }))
                }
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              >
                <option value="">— None (standalone team) —</option>
                {orgsData?.items?.map((org) => (
                  <option key={org.public_id} value={org.public_id}>
                    {org.name} ({org.public_id})
                  </option>
                ))}
              </select>
            </div>
            <TeamAccessChooser
              value={access}
              onChange={setAccess}
              plans={plansData?.plans ?? []}
              disabled={createTeam.isPending}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createTeam.isPending ||
                  !createForm.name.trim() ||
                  !createForm.creatorEmail.trim()
                }
              >
                {createTeam.isPending
                  ? "Creating..."
                  : teamAccessSubmitLabel(access, selectedPlanName)}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {(() => {
        const copy = teamAccessConfirmCopy(access, createForm.name.trim(), selectedPlanName)
        return (
          <ConfirmDialog
            open={showAccessConfirm}
            onOpenChange={setShowAccessConfirm}
            title={copy?.title ?? "Create team?"}
            description={copy?.description ?? ""}
            confirmLabel={copy?.confirmLabel ?? "Create team"}
            onConfirm={() => {
              setShowAccessConfirm(false)
              void runCreate()
            }}
          />
        )
      })()}
    </Layout>
  )
}
